import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CancelOutlined, CheckCircleOutline, Edit } from '@mui/icons-material';
import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import { PlanChip } from '@components/admin/PlanChip.tsx';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import { adminUsersService, AdminExpertUser, AdminPlanOption } from '@services/admin-users.service.ts';
import { toastService } from '@services/toast.service.ts';
import { AdminChangePlanModal } from '@components/admin/AdminChangePlanModal.tsx';

// Дублікат AdminExpertsPage.tsx, урізаний лише до білінгових полів (тариф + billingExempt).
// Живе окремо в блоці "Фінанси" — навмисно, без переносу з Users-сторінки (див. workspace/changelog).

const AdminFinanceExpertsPageContent: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [experts, setExperts] = useState<AdminExpertUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [billingToggling, setBillingToggling] = useState<number | null>(null);

  const [planOptions, setPlanOptions] = useState<AdminPlanOption[]>([]);
  const [planModalExpert, setPlanModalExpert] = useState<AdminExpertUser | null>(null);

  const fetchExperts = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminUsersService.getExperts({
        page,
        limit,
        search: searchQuery || undefined,
        planCode: planFilter || undefined,
      });
      setExperts(result.data);
      setTotal(result.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити список експертів.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchQuery, planFilter]);

  useEffect(() => {
    fetchExperts();
  }, [fetchExperts]);

  useEffect(() => {
    adminUsersService.getExpertPaymentsOptions().then((opts) => setPlanOptions(opts.plans));
  }, []);

  const handlePlanChange = async (planId: number) => {
    if (!planModalExpert) return;
    await adminUsersService.setExpertPlan(planModalExpert.id, planId);
    const plan = planOptions.find((p) => p.value === planId);
    setExperts((prev) => prev.map((e) =>
      e.id === planModalExpert.id
        ? { ...e, subscription: plan ? { planName: plan.label, planCode: plan.code, status: 'active' } : e.subscription }
        : e,
    ));
    toastService.success('Тарифний план змінено.');
  };

  const handleBillingToggle = async (expert: AdminExpertUser) => {
    setBillingToggling(expert.id);
    const newValue = !expert.billingExempt;
    try {
      await adminUsersService.setBillingExempt(expert.id, newValue);
      setExperts((prev) => prev.map((e) => e.id === expert.id ? { ...e, billingExempt: newValue } : e));
      toastService.success(newValue ? 'Білінг вимкнено для експерта.' : 'Білінг увімкнено для експерта.');
    } catch {
      toastService.error('Не вдалося змінити налаштування білінгу.');
    } finally {
      setBillingToggling(null);
    }
  };

  return (
    <Box data-i-doc={PageIndices.ADMIN_FINANCE_EXPERTS}>
      <PageHint iDoc={PageIndices.ADMIN_FINANCE_EXPERTS} />

      {/* ТИМЧАСОВО (Apple App Store review) — див. workspace/TECH_DEBT.md
          ("iOS billing bypass"). Прибрати банер разом з рештою хардкоду. */}
      <Alert severity="warning" sx={{ mb: 2 }}>
        Тимчасово вимкнено білінг для клієнтів на iOS — усі iOS-користувачі зараз отримують PRO-доступ незалежно від реальної підписки (Apple App Store review). Дані на цій сторінці не відображають цей стан.
      </Alert>

      <PageCard>
        <PageToolbar
          storageKey="filters:admin-finance-experts"
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук за іменем або email',
              value: searchQuery,
              onChange: (v) => { setSearchQuery(v); setPage(1); },
              flexGrow: 1,
            },
            {
              id: 'planCode',
              type: 'select',
              label: 'Тариф',
              value: planFilter,
              onChange: (v) => { setPlanFilter(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                ...planOptions.map((p) => ({ value: p.code, label: p.label })),
              ],
            },
          ]}
          onReset={() => { setSearchQuery(''); setPlanFilter(''); setPage(1); }}
          showReset
        />

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : experts.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: 'center' }}>Експертів не знайдено.</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Аватар</TableCell>
                  <TableCell>Ім'я / Email / Телефон</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Тариф</TableCell>
                  <TableCell>Білінг вимкнено</TableCell>
                  <TableCell>Реєстрація</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {experts.map((expert) => {
                  const isActive = expert.status === 1;
                  return (
                    <TableRow key={expert.id}>
                      <TableCell>
                        <UserAvatar path={expert.avatar} name={expert.name} sx={{ width: 32, height: 32 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{expert.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">{expert.email}</Typography>
                          {expert.isEmailVerified ? (
                            <Tooltip title="Email верифіковано">
                              <CheckCircleOutline color="success" sx={{ fontSize: 13 }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Email не верифіковано">
                              <CancelOutlined color="disabled" sx={{ fontSize: 13 }} />
                            </Tooltip>
                          )}
                        </Box>
                        {expert.phone && (
                          <Typography variant="caption" color="text.secondary">{expert.phone}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isActive ? 'Активний' : 'Заблокований'}
                          color={isActive ? 'success' : 'error'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PlanChip subscription={expert.subscription} />
                          <Tooltip title="Тимчасово вимкнено — керування тарифом перенесено на сторінку «Експерти» (Користувачі)">
                            <span>
                              <IconButton size="small" disabled>
                                <Edit fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={expert.billingExempt ? 'Білінг вимкнено (exempt)' : 'Білінг увімкнено'}>
                          <span>
                            <Switch
                              checked={expert.billingExempt}
                              onChange={() => handleBillingToggle(expert)}
                              disabled={billingToggling === expert.id}
                              size="small"
                              color="warning"
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{new Date(expert.createdAt).toLocaleDateString('uk-UA')}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => { setLimit(v); setPage(1); }}
          storageKey="pagination:admin-finance-experts"
        />
      </PageCard>

      <AdminChangePlanModal
        open={!!planModalExpert}
        onClose={() => setPlanModalExpert(null)}
        onSave={handlePlanChange}
        plans={planOptions}
        currentPlanCode={planModalExpert?.subscription?.planCode ?? null}
        userName={planModalExpert?.name ?? ''}
      />
    </Box>
  );
};

export const AdminFinanceExpertsPage: React.FC = () => (
  <DashboardLayout>
    <AdminFinanceExpertsPageContent />
  </DashboardLayout>
);
